import { useAuthStore } from '@/stores/auth-store';
import type { Permission, UserRole } from '@asi/shared';

export interface UsePermissionsReturn {
  role: UserRole | null;
  permissions: Permission[];
  can: (permission: Permission) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  isAtLeast: (minRole: UserRole) => boolean;
}

const ROLE_HIERARCHY: UserRole[] = ['client', 'patrol', 'operator', 'admin', 'super_admin'];

export function usePermissions(): UsePermissionsReturn {
  const profile = useAuthStore((s) => s.profile);
  const rolePermissions = useAuthStore((s) => s.rolePermissions);
  const role = profile?.role ?? null;

  // super_admin bypasses all permission checks unconditionally
  const isSuperAdmin = role === 'super_admin';

  const can = (permission: Permission): boolean =>
    isSuperAdmin || rolePermissions.includes(permission);

  const canAny = (perms: Permission[]): boolean =>
    isSuperAdmin || perms.some((p) => rolePermissions.includes(p));

  const hasRole = (target: UserRole | UserRole[]): boolean => {
    if (!role) return false;
    return Array.isArray(target) ? target.includes(role) : role === target;
  };

  const isAtLeast = (minRole: UserRole): boolean => {
    if (!role) return false;
    return ROLE_HIERARCHY.indexOf(role) >= ROLE_HIERARCHY.indexOf(minRole);
  };

  return { role, permissions: rolePermissions, can, canAny, hasRole, isAtLeast };
}
